import { useState, useMemo, useCallback, useEffect } from 'react';
import * as statUtils from '../core/st';
import Build from '../core/Build';
import * as scoreCalc from '../core/scoreCalculation';
import { prefetchItemCatalog } from '../services/itemCatalog';
import SpellService from '../services/SpellService';
import { getTypeId } from '../core/spellUtils';
import {
    computeSpellTermsForBuild,
    computeWeaponTermForBuild,
    spellLineCriterionSignature,
    buildSpellLineIndicatorMap,
    formatStatCriterionLabel,
    capitalizeCriterionStatLabel,
    localizedName,
    formatDataTabCriteriaTooltip,
} from '../core/spellScoreTerms';
import { scoreToColor } from '../utils/colorUtils';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { buildTagIds, normTagIds, sameTagIds } from '../core/criteriaTags';
import { isStatWeightKey, spellWeightKey, statWeightKey, WEAPON_WEIGHT_KEY } from '../core/criteriaWeights';
import { WEAPON_GROUP_IMG } from '../core/buildUtils';
import { bestWeaponMetricLabel } from '../core/bestWeaponMetric';

const TXT_WEAPON = { fr: 'Arme', en: 'Weapon', de: 'Waffe', es: 'Arma', pt: 'Arma' };
const TXT_BEST_WEAPON = {
    fr: 'Arme automatique',
    en: 'Automatic weapon',
    de: 'Automatische Waffe',
    es: 'Arma automática',
    pt: 'Arma automática',
};

// Fallback used when a build has no level set yet. Mirrors the same constant
// in Generator.js and hooks/useBuildPanelControls.js (cluster D) — this hook
// needs its own `lvl` derivation (see note below) rather than taking D's
// value as a prop.
const DEFAULT_LVL = 200;
const EMPTY_SPELL_SCORING_DATA = { spells: [], other: [], summonCatalog: {}, loadedTypeId: null };
const TXT_INFINITY = String.fromCharCode(0x221E);

/**
 * Criteria sheet, targets & score for the Generator page — spell/weapon
 * scoring data, the active-criteria list (stat/spell/weapon chips with their
 * toggle/target/remove handlers), the computed build score, and the
 * stats/spells criteria-reset actions. See docs/generator-slicing.md
 * cluster B.
 *
 * `lvl` coupling with cluster D (hooks/useBuildPanelControls.js): D needs
 * this hook's `spellScoringData`/`spellTypeId` as inputs (for its level-prune
 * effect), while this hook's score memos (`spellScoreDebug`/
 * `weaponScoreDebug`/`buildScoreResult`) need a `lvl` value. Rather than
 * introduce a call-order cycle between the two hooks, this hook derives its
 * own `lvl` with the exact same trivial formula D uses
 * (`build?.lvl || DEFAULT_LVL`) instead of taking D's `lvl` as a prop. Both
 * values are always identical since they come from the same `build`; callers
 * still get the "official" `lvl` from useBuildPanelControls for the rest of
 * the page (BuildPanel, DataHeaderSection, SpellsPanel, onLevelChange).
 *
 * `criteriaTagIds` has three writers total: this hook's own sync effect
 * (build loads while not generating) and its `syncCriteriaTags` effect (on
 * build/spellTypeId change) both live here — but the finished-run write in
 * `applyBuildFromPayload` lives in cluster A (generation run control), which
 * hasn't been extracted yet and stays inline in Generator.js. This hook
 * returns `setCriteriaTagIds` so Generator.js can keep wiring that third
 * writer through unchanged.
 */
export function useCriteriaAndScore({
    build,
    setBuild,
    canEditBuild,
    canEditGenerator,
    isGenerationActive,
    genConfig,
    setGenConfig,
    lang,
    statsTabLabel,
    spellsTabLabel,
}) {
    const activeTargetsCount = useMemo(() => {
        if (!build) return 0;
        return build.getSolverTargetCount();
    }, [build]);

    const spellTabCriteriaCount = useMemo(() => {
        if (!build) return 0;
        return build.getSpellSolverTargetCount() + build.getWeaponSolverTargetCount();
    }, [build]);

    // Only non-×1 entries are ever stored, so counting keys counts the criteria
    // the user actually reweighted — split by the tab that shows them.
    const statWeightsCount = useMemo(() => (
        Object.keys(build?.criteriaWeights || {}).filter(isStatWeightKey).length
    ), [build]);
    const spellWeightsCount = Object.keys(build?.criteriaWeights || {}).length - statWeightsCount;

    const spellTypeId = useMemo(() => {
        const clsName = build?.getCharacterClass?.()?.name || build?.characterClass?.name;
        return getTypeId(clsName);
    }, [build]);

    // See module note above: derived independently of cluster D's `lvl`.
    const lvl = useMemo(() => build?.lvl || DEFAULT_LVL, [build]);

    const [spellScoringData, setSpellScoringData] = useState(EMPTY_SPELL_SCORING_DATA);
    const [criteriaTagIds, setCriteriaTagIds] = useState(() =>
        normTagIds(build?.criteriaTags)
    );
    // Sync tags when a build loads outside of generation (initial load, navigation).
    useEffect(() => {
        if (!isGenerationActive) {
            setCriteriaTagIds(normTagIds(build?.criteriaTags));
        }
    }, [build, isGenerationActive]);

    const dataTabCriteriaTooltips = useMemo(() => (
        {
            stats: formatDataTabCriteriaTooltip(statsTabLabel, activeTargetsCount, lang),
            spells: formatDataTabCriteriaTooltip(spellsTabLabel, spellTabCriteriaCount, lang),
        }
    ), [statsTabLabel, spellsTabLabel, lang, activeTargetsCount, spellTabCriteriaCount]);

    useEffect(() => {
        if (!spellTypeId) return;
        SpellService.prefetchClassSpells(spellTypeId);
    }, [spellTypeId]);

    // Warm the item catalog while the generator settles. Slot search reads it,
    // and paying for it here rather than on the first keystroke is the whole
    // difference between local search feeling instant and feeling slower than
    // the request it replaced.
    useEffect(() => {
        prefetchItemCatalog();
    }, []);

    useEffect(() => {
        let alive = true;
        // Prevent stale class spell data from pruning criteria while new class data is still loading.
        setSpellScoringData(EMPTY_SPELL_SCORING_DATA);
        if (!spellTypeId) {
            return () => { };
        }
        const cached = SpellService.getCachedClassSpells(spellTypeId);
        if (cached && alive) {
            setSpellScoringData({
                spells: Array.isArray(cached.spells) ? cached.spells : [],
                other: Array.isArray(cached.other) ? cached.other : [],
                summonCatalog: cached.summonCatalog && typeof cached.summonCatalog === 'object' ? cached.summonCatalog : {},
                loadedTypeId: spellTypeId,
            });
        }
        SpellService.getClassSpells(spellTypeId).then((data) => {
            if (!alive) return;
            setSpellScoringData({
                spells: Array.isArray(data?.spells) ? data.spells : [],
                other: Array.isArray(data?.other) ? data.other : [],
                summonCatalog: data?.summonCatalog && typeof data.summonCatalog === 'object' ? data.summonCatalog : {},
                loadedTypeId: spellTypeId,
            });
        }).catch(() => {
            if (!alive) return;
            setSpellScoringData(EMPTY_SPELL_SCORING_DATA);
        });
        return () => { alive = false; };
    }, [spellTypeId]);

    useEffect(() => {
        let stopped = false;
        const syncCriteriaTags = async () => {
            if (!canEditBuild || !build || typeof build.setCriteriaTags !== 'function') return;
            let spellById = {};
            const hasSpellIds = Array.isArray(build.maximizeSpellIds) && build.maximizeSpellIds.length > 0;
            const hasSpellCriteria = Array.isArray(build.maximizeSpellCriteria) && build.maximizeSpellCriteria.length > 0;
            const needSpellData = hasSpellIds || hasSpellCriteria;
            if (needSpellData && spellTypeId) {
                try {
                    const data = await SpellService.getClassSpells(spellTypeId);
                    const list = [
                        ...(Array.isArray(data?.spells) ? data.spells : []),
                        ...(Array.isArray(data?.other) ? data.other : []),
                    ];
                    spellById = list.reduce((acc, s) => {
                        if (s?.id != null) acc[s.id] = s;
                        return acc;
                    }, {});
                } catch {
                    spellById = {};
                }
            }
            if (stopped) return;
            const nextTags = buildTagIds({ build, spellById });
            if (sameTagIds(build.criteriaTags, nextTags)) return;
            setBuild(build.setCriteriaTags(nextTags));
        };
        syncCriteriaTags();
        return () => { stopped = true; };
    }, [build, canEditBuild, setBuild, spellTypeId]);

    const spellScoreDebug = useMemo(() => (
        computeSpellTermsForBuild({
            build, spellTypeId, spellScoringData, lvl, lang, position: genConfig.position,
            useBoosts: genConfig.useBoostsForSpells !== false, includeDebug: false,
        })
    ), [build, spellTypeId, spellScoringData, lvl, lang, genConfig.position, genConfig.useBoostsForSpells]);

    const activeCriteria = useMemo(() => {
        if (!build || !canEditGenerator) return [];
        const out = [];
        const statsMap = build.getStats?.() || {};
        const solverTargets = build.getSolverTargets?.() || {};
        Object.entries(solverTargets).forEach(([rawId, rule]) => {
            const id = Number(rawId);
            const statDef = statUtils.STATS.find((s) => Number(s.id) === id);
            if (!statDef) return;
            const floor = Build.getEffectiveMinRange(statDef, build.lvl);
            const step = Number(statDef.step) || 1;
            const maxCap = Number(statDef.maxRange) || 9999;
            const isMaximize = rule?.maximize === true;
            const statObj = statsMap[id] || {};
            const curValue = Number.isFinite(Number(statObj.current)) ? Number(statObj.current) : floor;
            const curDesired = Number.isFinite(Number(statObj.desired)) ? Number(statObj.desired) : floor;
            const targetValue = isMaximize ? Math.max(floor, curDesired) : Math.round(Number(rule.min));
            // AP/MP default to a specific max target rather than maximize.
            const seedsToMax = id === statUtils.AP_ID || id === statUtils.MP_ID;
            out.push({
                key: `stat-${id}`,
                kind: 'stat',
                statId: id,
                icon: statDef.icon,
                name: capitalizeCriterionStatLabel(statDef?.label?.[lang] || statDef?.label?.en || String(id)),
                label: formatStatCriterionLabel(statDef, rule, lang),
                targetLabel: isMaximize ? TXT_INFINITY : null,
                weight: build.getCriterionWeight(statWeightKey(id)),
                onSetWeight: (w) => setBuild(build.setCriterionWeight(statWeightKey(id), w)),
                isMaximize,
                targetValue: Number.isFinite(targetValue) ? targetValue : floor,
                minValue: floor,
                maxValue: maxCap,
                step,
                // Gen-bar editor: flip between maximize (∞) and a numeric target.
                onToggleMaximize: (next) => {
                    if (next) {
                        setBuild(build.setMaximizeStat(id, true));
                        return;
                    }
                    const seed = seedsToMax
                        ? maxCap
                        : Math.min(maxCap, Math.max(floor + step, Math.round(curValue) || floor + step));
                    setBuild(build.setMaximizeStat(id, false).setDesired(id, seed));
                },
                onSetTarget: (value) => {
                    const clamped = Math.max(floor, Math.min(Number(value) || floor, maxCap));
                    setBuild(build.setMaximizeStat(id, false).setDesired(id, clamped));
                },
                onRemove: () => {
                    if (isMaximize) {
                        setBuild(build.setMaximizeStat(id, false));
                        return;
                    }
                    setBuild(build.setDesired(id, null));
                },
            });
        });

        // `spellScoringData`/`getWeapon()` hand back RAW records (spell
        // `icon_url`, item `icon`) whose media paths are relative — unlike the
        // spell tab, which reads view models already resolved in spellUtils.
        // Resolve here so the chips point at the media host instead of 404ing
        // against the app origin. `/img/...` fallbacks pass through untouched.
        const spellById = new Map([
            ...(Array.isArray(spellScoringData.spells) ? spellScoringData.spells : []),
            ...(Array.isArray(spellScoringData.other) ? spellScoringData.other : []),
        ].map((spell) => [Number(spell?.id), spell]));
        const spellCriteria = typeof build.getMaximizeSpellCriteria === 'function'
            ? build.getMaximizeSpellCriteria()
            : [];
        // Group criteria chips by the spell tab's display order (by element) so
        // e.g. all fire spells sit together. Criteria of the same spell keep their
        // relative order (stable sort); unknown spells sink to the end.
        const cardOrderById = spellScoreDebug?.spellCardOrderById || null;
        const spellIdOfCriterion = (c) => Number(
            typeof c === 'number' ? c : (c?.spell_id ?? c?.spellId ?? c?.id)
        );
        const orderedSpellCriteria = (Array.isArray(spellCriteria) ? [...spellCriteria] : []);
        if (cardOrderById) {
            orderedSpellCriteria.sort((a, b) => {
                const ao = cardOrderById.get(spellIdOfCriterion(a)) ?? Number.MAX_SAFE_INTEGER;
                const bo = cardOrderById.get(spellIdOfCriterion(b)) ?? Number.MAX_SAFE_INTEGER;
                return ao - bo;
            });
        }
        orderedSpellCriteria.forEach((criterion, idx) => {
            const spellId = Number(
                typeof criterion === 'number'
                    ? criterion
                    : (criterion?.spell_id ?? criterion?.spellId ?? criterion?.id)
            );
            if (!Number.isFinite(spellId)) return;
            const spell = spellById.get(spellId);
            const label = localizedName(spell?.name, lang, `Spell ${spellId}`);
            out.push({
                key: `spell-${spellId}-${idx}`,
                kind: 'spell',
                icon: resolveMediaUrl(spell?.icon || spell?.icon_url) || '/img/effet_spell.png',
                label,
                targetLabel: TXT_INFINITY,
                weight: build.getCriterionWeight(spellWeightKey(criterion)),
                onSetWeight: (w) => setBuild(build.setCriterionWeight(spellWeightKey(criterion), w)),
                spellCriterionSig: spellLineCriterionSignature(criterion),
                onRemove: () => {
                    if (criterion && typeof criterion === 'object') {
                        setBuild(build.toggleMaximizeSpellLine(criterion));
                    } else {
                        setBuild(build.toggleMaximizeSpell(spellId));
                    }
                },
            });
        });

        if (build.maximizeWeapon) {
            const weapon = build.getWeapon?.();
            out.push({
                key: 'weapon',
                kind: 'weapon',
                icon: resolveMediaUrl(weapon?.icon) || '/img/slot_arme.png',
                label: localizedName(weapon?.name, lang, TXT_WEAPON[lang] || TXT_WEAPON.en),
                targetLabel: TXT_INFINITY,
                weight: build.getCriterionWeight(WEAPON_WEIGHT_KEY),
                onSetWeight: (w) => setBuild(build.setCriterionWeight(WEAPON_WEIGHT_KEY, w)),
                onRemove: () => setBuild(build.clearMaximizeWeapon()),
            });
        }

        if (build.maximizeBestWeapon) {
            // Both halves stay the criterion rather than the weapon: this asked
            // for the best one, so the chip keeps saying that even after a run
            // has filled the slot. Wearing the found weapon's icon would make
            // it indistinguishable from the equipped-weapon chip above, which
            // is the opposite request.
            out.push({
                key: 'best-weapon',
                kind: 'weapon',
                // No artwork stands for "whichever weapon turns out best", so
                // the chip wears the mark for weapons as a kind, under the
                // generator's sparkles — the mark its card carries in the
                // spells panel. The weapon slot's own placeholder is a single
                // sword, which read as a weapon the build already has —
                // which is the other criterion.
                icon: WEAPON_GROUP_IMG,
                spark: true,
                label: TXT_BEST_WEAPON[lang] || TXT_BEST_WEAPON.en,
                // Where another criterion says what it is aiming at, this one
                // says what it is ranking on — the same slot, the same styling,
                // and more use than an infinity sign the chip above already
                // carries.
                targetLabel: bestWeaponMetricLabel(build.getBestWeaponMetric?.(), lang),
                weight: build.getCriterionWeight(WEAPON_WEIGHT_KEY),
                onSetWeight: (w) => setBuild(build.setCriterionWeight(WEAPON_WEIGHT_KEY, w)),
                onRemove: () => setBuild(build.clearMaximizeBestWeapon()),
            });
        }

        return out;
    }, [build, canEditGenerator, lang, setBuild, spellScoringData, spellScoreDebug]);

    const spellLineIndicatorMap = useMemo(
        () => buildSpellLineIndicatorMap(
            build?.getMaximizeSpellCriteria?.() || [],
            spellScoreDebug?.lineOrderBySpell || null,
        ),
        [build, spellScoreDebug]
    );

    const weaponScoreDebug = useMemo(() => (
        computeWeaponTermForBuild({
            build, lvl, lang, position: genConfig.position,
            useBoosts: genConfig.useBoostsForSpells !== false, includeDebug: false,
        })
    ), [build, lvl, lang, genConfig.position, genConfig.useBoostsForSpells]);

    const spellScoreTerms = useMemo(
        () => [...(spellScoreDebug?.terms || []), ...(weaponScoreDebug?.terms || [])],
        [spellScoreDebug, weaponScoreDebug]
    );

    // Positionally matched to spellScoreTerms — computeScore zips the two, so
    // they must be concatenated in the same order.
    const spellScoreImportance = useMemo(
        () => [...(spellScoreDebug?.importance || []), ...(weaponScoreDebug?.importance || [])],
        [spellScoreDebug, weaponScoreDebug]
    );

    const buildScoreResult = useMemo(() => {
        if (!build) return null;
        const targets = scoreCalc.targetsFromBuild(build);
        return scoreCalc.computeScore(build, targets, {
            allowPower: genConfig.allowPower,
            critDamCountsAsDamage: genConfig.critDamCountsAsDamage,
            spellTerms: spellScoreTerms,
            spellImportance: spellScoreImportance,
            spellTermDetails: [
                ...(spellScoreDebug?.details || []),
                ...(weaponScoreDebug?.details || []),
            ],
            defenseShare: genConfig.defenseShare,
            position: genConfig.position,
            useBoosts: genConfig.useBoostsForStats === true,
        });
    }, [build, genConfig.allowPower, genConfig.critDamCountsAsDamage, genConfig.defenseShare, genConfig.position, genConfig.useBoostsForStats, spellScoreTerms, spellScoreImportance, spellScoreDebug, weaponScoreDebug]);

    const headerScoreColor = buildScoreResult != null ? scoreToColor(buildScoreResult.score) : undefined;

    // Generic handler for toggling configuration settings
    const handleToggleSetting = useCallback((settingKey) => {
        if (!canEditGenerator) return;
        setGenConfig(genConfig.toggle(settingKey));
    }, [canEditGenerator, genConfig, setGenConfig]);

    const handleResetStatsCriteria = useCallback(() => {
        if (!build || !canEditGenerator) return;
        const next = build.resetActiveTargets();
        setBuild(next);
    }, [build, canEditGenerator, setBuild]);

    const handleResetDamageCriteria = useCallback(() => {
        if (!build || !canEditGenerator) return;
        const next = build.clearMaximizeSpells().clearMaximizeWeapon();
        setBuild(next);
    }, [build, canEditGenerator, setBuild]);

    const handleResetStatWeights = useCallback(() => {
        if (!build || !canEditGenerator) return;
        setBuild(build.clearCriteriaWeights(isStatWeightKey));
    }, [build, canEditGenerator, setBuild]);

    const handleResetSpellWeights = useCallback(() => {
        if (!build || !canEditGenerator) return;
        setBuild(build.clearCriteriaWeights((key) => !isStatWeightKey(key)));
    }, [build, canEditGenerator, setBuild]);

    return {
        spellTypeId,
        spellScoringData,
        criteriaTagIds,
        setCriteriaTagIds,
        activeTargetsCount,
        spellTabCriteriaCount,
        statWeightsCount,
        spellWeightsCount,
        dataTabCriteriaTooltips,
        activeCriteria,
        spellLineIndicatorMap,
        spellScoreTerms,
        buildScoreResult,
        headerScoreColor,
        handleToggleSetting,
        handleResetStatsCriteria,
        handleResetDamageCriteria,
        handleResetStatWeights,
        handleResetSpellWeights,
    };
}
